import React, { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import debounce from 'lodash/debounce';
import { useDispatch, useSelector } from 'react-redux';

import { IconButton, InputAdornment, TextField } from '@mui/material';
import { NgCard, NgCardContent } from '../UI';
import { setQuery, viewStateSelectors } from '../../store';
import { config } from '../../config';

const StyledSearchContainer = styled.div<{ $compact?: boolean }>`
  .MuiTextField-root {
    width: 100%;
  }

  ${({ $compact, theme }) =>
    $compact &&
    `
    .MuiInputBase-input {
      font-size: ${theme.fontSize.body};
    }
  `}
`;

interface Props {
  placeholder?: string;
  viewStateKey: string;
  noWrapper?: boolean;
  compact?: boolean;
}

export const NgFilters: FC<Props> = ({ viewStateKey, placeholder = 'Search', noWrapper = false, compact = false }) => {
  const dispatch = useDispatch();
  const storedQuery = useSelector(viewStateSelectors.getQuery(viewStateKey));
  const [localQuery, setLocalQuery] = useState(storedQuery);

  const debouncedDispatch = useRef(
    debounce((query: string) => {
      dispatch(setQuery({ viewStateKey, query }));
    }, config.defaultDebounce || 300),
  ).current;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalQuery(value);
    debouncedDispatch(value.trim());
  };

  const handleClear = () => {
    setLocalQuery('');
    debouncedDispatch.cancel();
    dispatch(setQuery({ viewStateKey, query: '' }));
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedDispatch.cancel();
    };
  }, [debouncedDispatch]);

  useEffect(() => {
    setLocalQuery(storedQuery);
  }, [storedQuery]);

  const searchInput = (
    <StyledSearchContainer $compact={compact}>
      <TextField
        placeholder={placeholder}
        value={localQuery}
        onChange={handleChange}
        size="small"
        sx={
          compact
            ? {
                '& .MuiInputBase-root': { height: '32px' },
                '& .MuiInputBase-input': { padding: '0 8px' },
              }
            : undefined
        }
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: localQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClear} edge="end">
                  <ClearIcon sx={{ fontSize: compact ? 14 : 20 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          },
        }}
      />
    </StyledSearchContainer>
  );

  if (noWrapper) {
    return searchInput;
  }

  return (
    <NgCard>
      <NgCardContent>{searchInput}</NgCardContent>
    </NgCard>
  );
};
